import { Head, router } from '@inertiajs/react'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { useState } from 'react'

// Extend dayjs with necessary plugins
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)

import { useMutation, useQuery } from '@tanstack/react-query'
import AdminLayout from '@/components/layouts/AdminLayout'
import { PageHeader } from '@/components/patterns'
import { Button, message } from '@/components/ui'
import { StatsSection } from './components/StatsSection'
import { EventsTableToolbar } from '@/components/events/EventsTableToolbar'
import { EventsTable } from '@/components/events/EventsTable'
import { ArchiveConfirmModal } from './components/ArchiveConfirmModal'
import { PlusCircleIcon } from '@/components/icons/EventsIcons'
import AssignOwnersModal from '@/components/modals/AssignOwnersModal'
import { api } from '@/lib/apiClient'
import { keys } from '@/lib/queryKeys'
import { Event, Category } from '@/types/events'
import { EventOwner } from '@/pages/admin/event_owners/index.page'
import { useEventFilters } from '@/hooks/useEventFilters'

interface EventsProps {
  event_owners?: EventOwner[]
  categories?: Category[]
}

export default function Events({ event_owners = [], categories = [] }: EventsProps) {
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState<boolean>(false)
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null)

  // Owner assignment modal state
  const [ownerModalVisible, setOwnerModalVisible] = useState<boolean>(false)
  const [currentEvent, setCurrentEvent] = useState<Event | null>(null)
  const [selectedEventOwners, setSelectedEventOwners] = useState<string[]>([])

  // Fetch events data
  const eventsQuery = useQuery({
    queryKey: keys.events.list(),
    queryFn: () => api.get<Event[]>('/admin/events'),
  })

  // Archive event mutation
  const archiveEventMutation = useMutation({
    mutationFn: (id: string) => {
      return api.patch<{ success: boolean }>(`/admin/events/${id}`, {
        event: { status: 'archived' },
      })
    },
    onSuccess: () => {
      eventsQuery.refetch()
      message.success('Event archived successfully')
      setDeleteConfirmVisible(false)
      setEventToDelete(null)
    },
    onError: (error: any) => {
      if (error.data && error.data.errors) {
        const errorMessages = error.data.errors.join(', ')
        message.error(`Failed to archive event: ${errorMessages}`)
      } else {
        message.error(`Failed to archive event: ${error.message}`)
      }
      setDeleteConfirmVisible(false)
      setEventToDelete(null)
    },
  })

  // Get events data from the query
  const events = eventsQuery.data || []

  // Use event filters hook
  const filters = useEventFilters(events)

  // Stats for the cards
  const registeredCount = events.reduce((sum, event) => sum + (event.registered || 0), 0)
  const approvedCount = events.reduce((sum, event) => sum + (event.approved || 0), 0)
  const pendingApprovalCount = events.reduce((sum, event) => sum + (event.pending_approval || 0), 0)

  // Handle edit button click
  const handleEdit = (event: Event) => {
    if (event.id) {
      router.visit(`/admin/events/${event.id}/edit`)
    }
  }

  // Handle delete button click
  const handleDelete = (event: Event) => {
    setEventToDelete(event)
    setDeleteConfirmVisible(true)
  }

  // Handle delete confirmation
  const confirmDelete = () => {
    if (eventToDelete?.id) {
      archiveEventMutation.mutate(eventToDelete.id)
    }
  }

  // Handle delete cancellation
  const cancelDelete = () => {
    setDeleteConfirmVisible(false)
    setEventToDelete(null)
  }

  // Handle owner avatar click
  const handleOwnerClick = (event: Event) => {
    setCurrentEvent(event)
    setSelectedEventOwners((event.owner_ids || []).map((id) => id.toString()))
    setOwnerModalVisible(true)
  }

  // Handle assign owners
  const handleAssignOwners = (eventId: string, ownerIds: string[]) => {
    api
      .patch(`/admin/events/${eventId}`, { event: { owner_ids: ownerIds } })
      .then(() => {
        message.success('Event owners updated successfully')
        setSelectedEventOwners(ownerIds)
        eventsQuery.refetch()
        setOwnerModalVisible(false)
      })
      .catch((error) => {
        console.error('Error updating event owners:', error)
        message.error('Failed to update event owners')
      })
  }

  return (
    <AdminLayout>
      <Head title="Events" />
      <div className="w-full bg-[#f5f5f5]">
        <PageHeader
          title="List of Events"
          subtitle="Create, manage and assign events."
          actions={
            <Button
              variant="primary"
              icon={<PlusCircleIcon />}
              onClick={() => router.visit('/admin/events/new')}
            >
              Create New Event
            </Button>
          }
        />

        <StatsSection
          registeredCount={registeredCount}
          approvedCount={approvedCount}
          pendingApprovalCount={pendingApprovalCount}
        />

        <EventsTableToolbar
          searchText={filters.searchText}
          onSearchChange={filters.setSearchText}
          activeTab={filters.activeTab}
          onTabChange={filters.setActiveTab}
          owners={event_owners}
          categories={categories}
          selectedOwners={filters.selectedOwners}
          onOwnersChange={filters.setSelectedOwners}
          selectedCategories={filters.selectedCategories}
          onCategoriesChange={filters.setSelectedCategories}
          selectedDateRange={filters.selectedDateRange}
          onDateRangeChange={filters.setSelectedDateRange}
          ownerFilterVisible={filters.ownerFilterVisible}
          onOwnerFilterVisibleChange={filters.setOwnerFilterVisible}
          categoryFilterVisible={filters.categoryFilterVisible}
          onCategoryFilterVisibleChange={filters.setCategoryFilterVisible}
          dateFilterVisible={filters.dateFilterVisible}
          onDateFilterVisibleChange={filters.setDateFilterVisible}
          onClearFilters={filters.clearFilters}
        />

        <EventsTable
          events={filters.filteredEvents}
          isLoading={eventsQuery.isLoading}
          isError={eventsQuery.isError}
          error={eventsQuery.error as Error}
          viewMode="list"
          categories={categories}
          owners={event_owners}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onView={(event) => router.visit(`/admin/events/${event.id}`)}
          onOwnerClick={handleOwnerClick}
        />

        <ArchiveConfirmModal
          visible={deleteConfirmVisible}
          event={eventToDelete}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          loading={archiveEventMutation.isPending}
        />

        <AssignOwnersModal
          visible={ownerModalVisible}
          onCancel={() => setOwnerModalVisible(false)}
          event={currentEvent}
          eventOwners={event_owners}
          initialSelectedOwners={selectedEventOwners}
          onAssign={handleAssignOwners}
        />
      </div>
    </AdminLayout>
  )
}
